import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';

interface DarkInfoTooltipProps {
	heading: string;
	body: string | string[];
	children: React.ReactElement;
}

/**
 * Dark, multi-paragraph info tooltip.
 *
 * Not using stylovyze's `StylovyzeTooltip` here because it is light-only (its styled tooltip hard-codes
 * `palette.common.white` with no `dark`/`variant` prop) and its `content` is a single string, whereas the
 * design spec requires a dark surface and some tooltips need multiple paragraphs. Track adding a dark
 * variant + `content: ReactNode` upstream in stylovyze so this wrapper can eventually be removed.
 */
export const DarkInfoTooltip = ({ heading, body, children }: DarkInfoTooltipProps): JSX.Element => {
	const paragraphs = Array.isArray(body) ? body : [body];
	return (
		<Tooltip
			arrow
			placement="bottom-start"
			// MUI caps the tooltip surface at 300px by default, so widen it here for the multi-paragraph bodies
			slotProps={{ tooltip: { sx: { maxWidth: 320 } } }}
			title={
				<Box sx={{ p: 0.5 }}>
					<Typography sx={{ fontWeight: 600, fontSize: '0.8125rem', mb: 0.75 }}>{heading}</Typography>
					{paragraphs.map((text, i) => (
						<Typography
							key={text}
							sx={{
								fontSize: '0.75rem',
								color: 'rgba(255,255,255,0.85)',
								mb: i < paragraphs.length - 1 ? 0.5 : 0,
							}}>
							{text}
						</Typography>
					))}
				</Box>
			}>
			{children}
		</Tooltip>
	);
};
